<template>
  <form @submit.prevent @keydown.enter="$event.preventDefault()">
    <div class="form">
      <CurrencyInput
        v-if="fiatInputs"
        key="buyFiat"
        name="fiat"
        type="text"
        label="Spend"
        placeholder="Fiat amount"
        :defaultValue="{
          fiatAmount: form.fiatAmount,
          fiatType: form.fiatType,
        }"
        :items="fiatInputs"
        :isLoading="isFetchingData.fiatType"
        :errors="store.getErrorByField('fiatAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <CurrencyInput
        v-if="coinInputs"
        key="buyCoin"
        name="coin"
        type="text"
        label="Receive"
        placeholder="Coin amount"
        :defaultValue="{
          coinAmount: form.coinAmount,
          coinType: form.coinType,
        }"
        :items="coinInputs"
        :isLoading="isFetchingData.coinType"
        :errors="store.getErrorByField('coinAmount')"
        @inputValueUpdate="updateCurrencyInput"
        @dropdownValueUpdate="updateDropdown"
      />

      <FormSelect
        type="text"
        name="blockchain"
        label="Selected blockchain"
        :key="'blockchain'"
        :disabled="disabledBlockchainSelect"
        :items="blockchainsByCoin"
        :defaultItem="defaultBlockchain"
        :isLoading="isFetchingData.blockchain"
        @dropdownValueUpdate="updateDropdown"
      />

      <ButtonBase
        :isDisabled="store.formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        Create Order
      </ButtonBase>
    </div>
  </form>
</template>

<script setup>
// ====================================
// Packages
// ====================================
import { ref, watch, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
// ====================================
// Storage
// ====================================
import { useBuyOrderFormStore } from "@/stores/forms/buyOrderFormStore";
import { useGlobalStore } from "@/stores/globalStore";
// ====================================
// Composables
// ====================================
import useDynamicPricingCalculator from "@/composables/helpers/useDynamicPricingCalculator";
import useOrderEndpoints from "@/composables/api/useOrderEndpoints";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useOrderForm from "@/composables/shared/useOrderForm";
// ====================================
// Components
// ====================================
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import CurrencyInput from "@/components/ui/inputs/CurrencyInput.vue";

const router = useRouter();

const { getFiats, getCoins } = useOrderEndpoints();
const { watchFormAndUpdateStore } = useWatchAndUpdate();
const { dynamicPricingCalculator } = useDynamicPricingCalculator();
const { findBlockchainsByCoin } = useOrderForm();

const store = useBuyOrderFormStore();
const globalStore = useGlobalStore();
const fiatResponse = ref([]);
const fiatInputs = ref([]);
const coinResponse = ref([]);
const coinInputs = ref([]);
const blockchainsByCoin = ref([]);
const defaultBlockchain = ref("");
const form = ref({
  fiatType: "",
});
const isFetchingData = ref({
  fiatAmount: true,
  coinAmount: true,
});

const handleFormSuccess = () => {
  router.push({
    path: "/",
    query: { ...store.getFormPayload, orderMode: "BUY" },
  });
};

// to set the validation check on the store for widget

const handleFormStoreKeyRequiredFieldForSubmitButton = (isRequiredFlag) => {
  const keysToSetRequiredFieldFor = ["payment", "walletAddress"];
  keysToSetRequiredFieldFor.forEach((key) =>
    store.setRequiredFieldByKey(key, isRequiredFlag)
  );
};

// todo handle success
const handleFormSubmit = () => {
  globalStore.setIsFormLoading(true);
  handleFormSuccess();
  handleFormStoreKeyRequiredFieldForSubmitButton(true);
};

const setDefaultsByLocalStorage = () => {
  Object.keys(store.form).forEach((key) => {
    if (key === "fiatType") {
      const defaultCoin =
        fiatInputs.value.indexOf("USD") >= 0 ? "USD" : fiatInputs.value[0];
      form.value.fiatType = defaultCoin;
    } else {
      form.value[key] = store.form[key].value;
    }
    const lockFiat = store.form.lockFiat.value;
    const fieldToTriggerCalcBy = lockFiat ? "fiatAmount" : "coinAmount";
    dynamicPricingCalculator(
      fieldToTriggerCalcBy,
      form.value[fieldToTriggerCalcBy],
      form,
      store
    );
  });
};

const updateCurrencyInput = (inputData) => {
  const field = Object.keys(inputData)[0];
  const value = Object.values(inputData)[0];
  const lockFiat = field === "fiatAmount";

  form.value[field] = value;
  form.value.lockFiat = lockFiat;

  dynamicPricingCalculator(field, value, form, store);
};

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  if (value) form.value[field] = value;

  dynamicPricingCalculator(field, value, form, store);
};

const disabledBlockchainSelect = computed(() => {
  if (
    blockchainsByCoin.value &&
    blockchainsByCoin.value.includes("No valid blockchains available")
  ) {
    return true;
  }

  return false;
});

watch(
  () => form.value.coinType,
  () => {
    store.setCoinType(form.value.coinType);
    findBlockchainsByCoin(store, form, defaultBlockchain, blockchainsByCoin);
    updateDropdown({ blockchain: blockchainsByCoin.value[0] });
  },
  { deep: true }
);

const changeLoadingValueByName = (name, isLoading) => {
  isFetchingData.value[name] = isLoading;
};

const runOnMounted = async () => {
  fiatResponse.value = await getFiats("buy");
  coinResponse.value = await getCoins("buy");
  await globalStore.fetchPayments();

  fiatInputs.value = fiatResponse.value.data.data.fiats.map((el) => {
    return el.fiat_code;
  });

  coinInputs.value = coinResponse.value.data.data.coins.map(
    (el) => el.coin_code
  );
  handleFormStoreKeyRequiredFieldForSubmitButton(false);
  store.setCoinData(coinResponse.value.data.data.coins);
  setDefaultsByLocalStorage();
  changeLoadingValueByName("fiatType", false);
  changeLoadingValueByName("coinType", false);
};

watch(
  () => globalStore.$state.token,
  (val) => {
    if (val) {
      runOnMounted();
    }
  },
  { deep: true, immediate: true }
);

watchFormAndUpdateStore(form, store, () => {});
</script>

<style lang="scss" scoped>
.form-group {
  padding: $padding-std;
  display: grid;
  grid-template-columns: 3fr 0.5fr;
  align-items: center;
  border-radius: 1rem;
  background: var(--input-background);
  margin-bottom: calc($formFieldSpacing/2);

  &__label {
    font-size: calc($font-body/1.1); //1.2rem;
    opacity: 0.6;
  }

  &__input {
    width: 100%;
    font-size: $font-sub-jumbo;
    opacity: 1;
    background: transparent;
    border: transparent;
  }

  .currency-flag {
    width: 25px;
    height: 25px;
    border-radius: 50%;
  }
}

.dropdown-select {
  min-height: 50px;
  &__selected-item {
    display: flex;

    &__icon {
      margin-inline: 0.75rem;
      &.coin {
        width: 24px;
      }
    }
  }
}

.text-fees {
  margin-bottom: $formFieldSpacing;
}
</style>
